import type React from "react";
import { Group, Stack, Text } from "@mantine/core";
import { Table } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import { MappingStatus, ReviewStatus, type RowEdit } from "@models/job";
import type { MappingResultRowTm } from "@models/mappingResult";
import type { RowSaveStatus } from "@hooks/useJobResults";
import { useResultRow } from "@hooks/jobResults/useResultRow";
import { PLACEHOLDER, resolveDisplayTarget } from "@utils/rowHelpers";
import RowStatusCell from "../RowStatusCell";
import RowFindingBadge from "../RowFindingBadge";
import TmRowActions from "./TmRowActions";
import SourcePdfLink from "../../SourcePdfLink";

interface TmJobResultsTableRowProps {
  row: MappingResultRowTm;
  edit: RowEdit | undefined;
  saveState: RowSaveStatus | undefined;
  jobId: string;
  hasInputPdf?: boolean;
  registerRef: (el: HTMLTableRowElement | null) => void;
  onAccept: (text?: string) => void;
  onReject: () => void;
  onRecover: () => void;
  onRetrySave: () => void;
}

/**
 * One technical-manual row: the value as extracted, and the catalogue value it
 * resolved to.
 *
 * No score column. TM's score is a cosine against a value the reviewer can
 * read in full two cells away — it offered a number to argue with rather than
 * evidence to judge by. What stands in its place is the "Exact Match" badge
 * under the value: an arithmetic decision over every value of the field, which
 * is either true or absent and cannot be misread as "61% right".
 */
const TmJobResultsTableRow: React.FC<TmJobResultsTableRowProps> = ({
  row,
  edit,
  saveState,
  jobId,
  hasInputPdf = false,
  registerRef,
  onAccept,
  onReject,
  onRecover,
  onRetrySave,
}) => {
  const { t } = useTranslation();
  const {
    effectiveStatus,
    isFailed,
    isSaving,
    actionsDisabled,
  } = useResultRow(row, edit, saveState);

  const { source } = row;
  const target = resolveDisplayTarget(row, edit);
  const isRejected = effectiveStatus === ReviewStatus.Rejected;
  const sourceValue = source.cleanedText || source.rawText;
  const variantQualifiers = Object.entries(source.variantQualifiers ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}: ${value}`)
    .join(" · ");
  const hasDistinctRawText =
    source.rawText != null && source.rawText !== source.cleanedText;
  const showSourceForMissingValue =
    row.mappingStatus === MappingStatus.NoMatch &&
    edit?.chosenRank == null &&
    row.chosenRank == null;

  const rowClass = "job-results-page__row";

  // Wrapped, not passed straight through: TmRowActions hands its button's
  // click event to the handler, which `onAccept(text?)` would take as the text.
  const handleAccept = (): void => {
    onAccept();
  };

  const dimmed = (
    <Text size="xs" c="dimmed">
      {PLACEHOLDER}
    </Text>
  );

  return (
    <Table.Tr
      ref={registerRef}
      className={rowClass}
      tabIndex={0}
      data-row-index={row.rowIndex}
    >
      {/* # */}
      <Table.Td className="job-results-page__tm-index-cell">
        <Text
          size="xs"
          c="dimmed"
          ff="monospace"
          fw={500}
          style={{ fontSize: "0.68rem" }}
        >
          {row.rowIndex + 1}
        </Text>
      </Table.Td>

      {/* FILE# identifies the original even when two files share a name. */}
      <Table.Td>
        <SourcePdfLink
          jobId={jobId}
          fileIndex={row.fileIndex}
          filename={source.sourceFilename}
          hasInputPdf={hasInputPdf}
        />
      </Table.Td>

      {/* Where in the manual the value was read from */}
      <Table.Td>
        {source.path ? (
          <Text size="sm" ff="monospace" lineClamp={2} title={source.path}>
            {source.path}
          </Text>
        ) : (
          dimmed
        )}
      </Table.Td>

      {/* The extracted value. The cleaned text is what was matched, so it
          leads; the raw extraction sits under it when the two differ. The
          source remark and country applicability follow it on one line. */}
      <Table.Td>
        {isFailed ? (
          <Text size="xs" c="red" lineClamp={2}>
            {row.error?.message ?? t("resultRow.status.failed")}
          </Text>
        ) : sourceValue ? (
          <Stack gap={2}>
            <Text
              size="sm"
              c="black"
              style={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              {sourceValue}
            </Text>
            {hasDistinctRawText && (
              <Text
                size="xs"
                c="dimmed"
                style={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {t("jobResultsPage.tmTable.rawAs", {
                  defaultValue: "raw: {{value}}",
                  value: source.rawText,
                })}
              </Text>
            )}
            {variantQualifiers && (
              <Text
                size="xs"
                c="dimmed"
                style={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {t("jobResultsPage.tmTable.variantQualifiers", {
                  defaultValue: "Qualifiers: {{value}}",
                  value: variantQualifiers,
                })}
              </Text>
            )}
            {(source.remark?.trim() || source.countryCodes?.trim()) && (
              <Group
                gap="sm"
                align="flex-start"
                wrap="wrap"
              >
                {source.remark?.trim() ? (
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {source.remark}
                  </Text>
                ) : null}
                {source.countryCodes?.trim() && (
                  <Text
                    size="xs"
                    c="dimmed"
                    ff="monospace"
                    ta="right"
                    style={{ whiteSpace: "nowrap", marginLeft: "auto" }}
                  >
                    {source.countryCodes}
                  </Text>
                )}
              </Group>
            )}
          </Stack>
        ) : (
          dimmed
        )}
      </Table.Td>

      {/* Item-MP Listing */}
      <Table.Td>
        {target?.itemMpListing ? (
          <Text size="sm" lineClamp={3}>
            {target.itemMpListing}
          </Text>
        ) : (
          dimmed
        )}
      </Table.Td>

      {/* ItemMP ID */}
      <Table.Td>
        {target?.itemMpId != null ? (
          <Text size="sm" ff="monospace">
            {target.itemMpId}
          </Text>
        ) : (
          dimmed
        )}
      </Table.Td>

      {/* The catalogued value, and directly under it what the pipeline
          concluded ABOUT that value: "Exact" when the catalogue holds it,
          "Not found" when it does not, "Not checked" when the catalogue
          could not be consulted. Read-only — changing it means picking
          another candidate. Findings are next to the value they describe;
          the Status column carries only what a HUMAN did. */}
      <Table.Td>
        {showSourceForMissingValue ? (
          sourceValue ? (
            <Stack gap={2} align="flex-start">
              <Text
                size="sm"
                ta="left"
                style={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {sourceValue}
              </Text>
              {hasDistinctRawText && (
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {t("jobResultsPage.tmTable.rawAs", {
                    defaultValue: "raw: {{value}}",
                    value: source.rawText,
                  })}
                </Text>
              )}
              <RowFindingBadge row={row} />
            </Stack>
          ) : (
            dimmed
          )
        ) : target?.valueDisplay ? (
          <Stack gap={2} align="flex-start">
            <Text
              size="sm"
              ta="left"
              style={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              {target.valueDisplay} {target.unit && `(${target.unit})`}
            </Text>
            <Group gap={4} wrap="wrap" align="center">
              <RowFindingBadge row={row} />
            </Group>
            {target.digitCheckFailed && (
              <Text size="xs" c="red">
                {t("jobResultsPage.tmTable.digitCheckFailed", {
                  defaultValue: "Check digits: source and catalogue values differ",
                })}
              </Text>
            )}
          </Stack>
        ) : (
          dimmed
        )}
      </Table.Td>

      {/* Status — the REVIEWER axis only. The pipeline's finding renders
          under the value it is about; see the value cell above. */}
      <RowStatusCell
        status={effectiveStatus}
        saveState={saveState}
        onRetrySave={onRetrySave}
      />

      <TmRowActions
        hasTopMatch={target !== null}
        actionsDisabled={actionsDisabled}
        isSaving={isSaving}
        isRejected={isRejected}
        onAccept={handleAccept}
        onReject={onReject}
        onRecover={onRecover}
      />

    </Table.Tr>
  );
};

export default TmJobResultsTableRow;
